import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import { useSession } from '../context/SessionContext';
import PageHeader from '../lib/ui/PageHeader';
import { EmptyState, Spinner } from '../lib/ui/Feedback';

const API = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

const REASON_LABELS = {
  referral_bonus: '🎁 Referral Bonus',
  first_order_cashback: '🎉 First Order Cashback',
  order_payment: '🛒 Order Payment',
  admin_credit: '💳 Credit by Admin',
  admin_debit: '🔻 Debit by Admin',
};
function txLabel(reason) {
  if (REASON_LABELS[reason]) return REASON_LABELS[reason];
  if (reason?.startsWith('admin:')) return `💳 ${reason.replace('admin:', '').trim()}`;
  return reason || 'Wallet Transaction';
}

export default function WalletPage() {
  const { lang } = useLang();
  const te = lang === 'te';
  const { sessionUser } = useSession();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState({ balance: 0, transactions: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionUser) { navigate('/signup', { replace: true }); return; }
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/wallet/${encodeURIComponent(sessionUser.phone)}`, {
          headers: sessionUser.token ? { Authorization: `Bearer ${sessionUser.token}` } : {},
        });
        setWallet({ balance: res.data.balance || 0, transactions: res.data.transactions || [] });
      } catch { /* wallet might not be set up yet */ } finally { setLoading(false); }
    })();
  }, [sessionUser]);

  if (!sessionUser) return null;

  return (
    <div className="max-w-2xl mx-auto">
      <PageHeader title={te ? 'వాలెట్' : 'Wallet'} fallback="/profile" />
      <div className="px-4 md:px-6 py-4">
        <div className="bg-gradient-to-br from-brand-dark to-brand rounded-2xl px-5 py-6 mb-5">
          <div className="text-[13px] text-white/80 font-bold mb-1">{te ? 'అందుబాటులో ఉన్న బ్యాలెన్స్' : 'Available Balance'}</div>
          <div className="text-4xl font-black text-white tracking-tight">₹{Number(wallet.balance).toFixed(2)}</div>
          <div className="text-xs text-white/70 mt-1.5">{te ? 'ఆర్డర్ చెల్లింపులకు వాడవచ్చు & క్యాష్‌బ్యాక్/రిఫరల్ ద్వారా సంపాదించవచ్చు' : 'Used for order payments & earned via cashback / referrals'}</div>
        </div>

        <div className="text-[11px] font-extrabold text-ink-faint uppercase tracking-wide mb-2.5">{te ? 'లావాదేవీల చరిత్ర' : 'Transaction History'}</div>

        {loading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : wallet.transactions.length === 0 ? (
          <EmptyState icon="💰" title={te ? 'లావాదేవీలు లేవు' : 'No transactions yet'} sub={te ? 'క్యాష్‌బ్యాక్ మరియు రిఫరల్ బోనస్‌లు ఇక్కడ కనిపిస్తాయి.' : 'Cashback and referral bonuses will appear here.'} />
        ) : (
          <div className="space-y-2">
            {wallet.transactions.map((tx, i) => (
              <div key={tx.id || i} className="flex items-center gap-3 bg-white border border-black/5 rounded-2xl px-3.5 py-3">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[13px] text-ink">{txLabel(tx.reason)}</div>
                  <div className="text-[11px] text-ink-faint mt-0.5">{new Date(tx.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                </div>
                <div className={`font-black shrink-0 ${tx.type === 'credit' ? 'text-brand' : 'text-red-600'}`}>{tx.type === 'credit' ? '+' : '−'}₹{Number(tx.amount).toFixed(2)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
